/**
 * Significance-testing settings, shared by the Query Builder (per builder
 * tab) and the Excel report (report default + per-tab override).
 *
 * The engine accepts `SIG:<L1>[,<L2>][,REST][,NOOVERLAP]` and `MINBASE:<n>`
 * on XTAB/DTAB. `formatSignificanceClause` is the only place that turns
 * these settings into query text; `ScriptGenerationService` appends it after
 * the `OPERATORS:` part.
 *
 * `undefined` settings mean "off" everywhere (and "inherit the report
 * default" for an Excel report tab), so saved builders and reports from
 * before this feature load unchanged.
 */

import { STATISTIC_TYPE } from "types/ReportingDashboard/elements.types"

export interface SignificanceSettings {
  enabled: boolean
  /** One or two confidence levels, stored descending (upper case letters
   *  for the first, lower case for the second). */
  levels: [number] | [number, number]
  /** Also compare each column with the rest of the sample (`REST`). */
  vsRest: boolean
  /** Overlap correction for respondents in more than one column. Off emits
   *  `NOOVERLAP`. */
  overlap: boolean
  /** Columns with an unweighted base below this are not tested. */
  minBase: number
}

export type SignificancePreset = "off" | "95" | "95_90"

/** The engine's min base when `MINBASE:` is not given. */
export const DEFAULT_MIN_BASE = 30

/**
 * The statistics the engine tests, per operation: XTAB tests `col%`, DTAB
 * tests `perc` (BOOL fields) and `avg` (INT/REAL fields). The script
 * emitter adds them to the operators while significance is on, and the
 * Builder locks their pills on.
 */
export const SIGNIFICANCE_TESTED_STATISTICS: Record<
  "XTAB" | "DTAB",
  STATISTIC_TYPE[]
> = {
  XTAB: [STATISTIC_TYPE.COL_PERCENT],
  DTAB: [STATISTIC_TYPE.PERC, STATISTIC_TYPE.AVG],
}

/** Level bounds the engine accepts (inclusive). */
export const MIN_LEVEL = 50
export const MAX_LEVEL = 99.99

export const SIGNIFICANCE_PRESETS: Record<
  SignificancePreset,
  SignificanceSettings
> = {
  off: {
    enabled: false,
    levels: [95],
    vsRest: true,
    overlap: true,
    minBase: DEFAULT_MIN_BASE,
  },
  "95": {
    enabled: true,
    levels: [95],
    vsRest: true,
    overlap: true,
    minBase: DEFAULT_MIN_BASE,
  },
  "95_90": {
    enabled: true,
    levels: [95, 90],
    vsRest: true,
    overlap: true,
    minBase: DEFAULT_MIN_BASE,
  },
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v)

const isValidLevel = (v: unknown): v is number =>
  typeof v === "number" &&
  Number.isFinite(v) &&
  v >= MIN_LEVEL &&
  v <= MAX_LEVEL

/**
 * Turns whatever was loaded (a saved report, a backend builder sidecar,
 * localStorage) into valid settings, or `undefined` when it isn't a settings
 * object at all. Garbage in a field falls back to the "95" preset's value
 * for that field:
 *   - `enabled` must be a boolean, otherwise the whole value is rejected.
 *   - `levels`: valid levels (numbers in [50, 99.99]) are kept, de-duplicated,
 *     sorted descending and capped at two; none valid gives `[95]`.
 *   - `vsRest` / `overlap`: booleans, else `true`.
 *   - `minBase`: a finite number >= 0, floored to an integer, else 30.
 */
export const normalizeSignificanceSettings = (
  s: unknown
): SignificanceSettings | undefined => {
  if (!isRecord(s) || typeof s.enabled !== "boolean") return undefined

  const fallback = SIGNIFICANCE_PRESETS["95"]
  const rawLevels = Array.isArray(s.levels) ? s.levels : []
  const distinct = Array.from(new Set(rawLevels.filter(isValidLevel))).sort(
    (a, b) => b - a
  )
  const levels: SignificanceSettings["levels"] =
    distinct.length >= 2
      ? [distinct[0], distinct[1]]
      : distinct.length === 1
      ? [distinct[0]]
      : [...fallback.levels]

  const minBase =
    typeof s.minBase === "number" && Number.isFinite(s.minBase) && s.minBase >= 0
      ? Math.floor(s.minBase)
      : fallback.minBase

  return {
    enabled: s.enabled,
    levels,
    vsRest: typeof s.vsRest === "boolean" ? s.vsRest : fallback.vsRest,
    overlap: typeof s.overlap === "boolean" ? s.overlap : fallback.overlap,
    minBase,
  }
}

const sameSettings = (a: SignificanceSettings, b: SignificanceSettings) =>
  a.enabled === b.enabled &&
  a.levels.length === b.levels.length &&
  a.levels.every((level, i) => level === b.levels[i]) &&
  a.vsRest === b.vsRest &&
  a.overlap === b.overlap &&
  a.minBase === b.minBase

/** Which preset the settings match: `"off"` for undefined, disabled or
 *  invalid settings, `"custom"` when an enabled setting matches no preset.
 *  The input is normalized first (values rehydrated from storage are not),
 *  so a partial object never throws. */
export const presetOf = (
  s: SignificanceSettings | undefined
): SignificancePreset | "custom" => {
  const n = normalizeSignificanceSettings(s)
  if (!n || !n.enabled) return "off"
  if (sameSettings(n, SIGNIFICANCE_PRESETS["95"])) return "95"
  if (sameSettings(n, SIGNIFICANCE_PRESETS["95_90"])) return "95_90"
  return "custom"
}

/**
 * The query clause for the settings, with a leading space so it can be
 * appended to an XTAB/DTAB line: `""` when off, otherwise
 * ` SIG:95[,90][,REST][,NOOVERLAP][ MINBASE:n]`. `MINBASE` is only emitted
 * when it differs from the engine default (30). The input is normalized
 * first, so levels always come out descending.
 */
export const formatSignificanceClause = (
  s: SignificanceSettings | undefined
): string => {
  const n = normalizeSignificanceSettings(s)
  if (!n || !n.enabled) return ""
  const parts: string[] = n.levels.map(String)
  if (n.vsRest) parts.push("REST")
  if (!n.overlap) parts.push("NOOVERLAP")
  const minBase =
    n.minBase !== DEFAULT_MIN_BASE ? ` MINBASE:${n.minBase}` : ""
  return ` SIG:${parts.join(",")}${minBase}`
}

/**
 * A copy of `obj` (a builder tab, a report tab, the report section) with its
 * `significance` replaced by the normalized `significance`. Anything that
 * isn't a settings object (undefined, garbage from an old save) removes the
 * key, so objects saved before the feature keep their exact shape.
 */
export const withSignificance = <
  T extends { significance?: SignificanceSettings },
>(
  obj: T,
  significance: unknown
): T => {
  const next: { significance?: SignificanceSettings } = { ...obj }
  const normalized = normalizeSignificanceSettings(significance)
  if (normalized) next.significance = normalized
  else delete next.significance
  return next as T
}

/** The settings an Excel report tab runs with: its own override, or the
 *  report default when the tab has none (undefined = inherit). */
export const resolveTabSignificance = (
  tab: SignificanceSettings | undefined,
  report: SignificanceSettings | undefined
): SignificanceSettings | undefined => tab ?? report
